/**
 * Phase 4 — GA4 integration page.
 *
 * Three states share one page so the user always lands somewhere
 * sensible regardless of where they came from (sidebar, OAuth
 * callback, deep link):
 *
 *   1. Not configured  — operator hasn't set GOOGLE_OAUTH_* env vars.
 *   2. Not connected   — show big "Connect Google Analytics" button.
 *   3. Token saved,
 *      property pending → show property dropdown to select one.
 *   4. Fully connected → show property + Disconnect + bot-traffic UI.
 *
 * The OAuth callback view redirects here with `?ga4=connected` or
 * `?ga4=error:…`; we surface a banner accordingly.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { ga4 } from "../../api/endpoints";
import DashboardFilters, {
  defaultDashboardFilters,
} from "../../components/DashboardFilters.jsx";
import { useSelectedWebsite } from "./Layout.jsx";

const BOT_COLOURS = [
  "#2ea7af", "#7c3aed", "#f97316", "#10b981", "#0ea5e9",
  "#ef4444", "#eab308", "#8b5cf6", "#14b8a6", "#f43f5e",
];

function colourForKey(key, supported) {
  const idx = supported.findIndex((b) => b.key === key);
  return BOT_COLOURS[(idx >= 0 ? idx : 0) % BOT_COLOURS.length];
}

export default function GA4Integration() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { websiteId: selectedFromCtx } = useSelectedWebsite() || {};
  // Effective website id: either the sidebar selection (most cases),
  // or the ?website_id query param the OAuth callback redirect carries.
  const websiteId = selectedFromCtx || Number(params.get("website_id")) || null;

  const [status, setStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(true);
  const [properties, setProperties] = useState(null);
  const [propertiesLoading, setPropertiesLoading] = useState(false);
  const [propertyError, setPropertyError] = useState(null);
  const [authorising, setAuthorising] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  const [filters, setFilters] = useState(() => defaultDashboardFilters(28));
  const [botFilter, setBotFilter] = useState("all");
  const [report, setReport] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportError, setReportError] = useState(null);

  // OAuth callback banner — read once on mount and stripped from the URL.
  const [oauthHint, setOauthHint] = useState(() => params.get("ga4") || "");
  const callbackHandled = useRef(false);
  useEffect(() => {
    if (oauthHint && !callbackHandled.current) {
      callbackHandled.current = true;
      // Strip the param so a refresh doesn't replay the banner.
      const next = new URLSearchParams(params);
      next.delete("ga4");
      next.delete("website_id");
      navigate(
        {
          search: next.toString() ? `?${next.toString()}` : "",
        },
        { replace: true },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reloadStatus = useCallback(async () => {
    if (!websiteId) return;
    setStatusLoading(true);
    try {
      const r = await ga4.status(websiteId);
      setStatus(r.data);
      // If the user has tokens but no property yet, auto-fetch the
      // property list so the dropdown appears without an extra click.
      if (r.data?.connected === false && r.data?.configured) {
        // Connected to Google but property not picked yet — try to detect
        // by attempting properties fetch.
      }
    } catch {
      setStatus(null);
    } finally {
      setStatusLoading(false);
    }
  }, [websiteId]);

  useEffect(() => {
    reloadStatus();
  }, [reloadStatus]);

  // Whenever status flips to "tokens stored but no property selected",
  // fetch the property list. We detect that state by trying GET /properties/
  // and treating 400 as "not yet authorised". The status endpoint only
  // reports `connected: true` after a property is chosen, so we lean on
  // the OAuth hint to decide whether to attempt the call.
  const needsProperty = oauthHint === "connected" && status && !status.connected;

  const fetchProperties = useCallback(async () => {
    if (!websiteId) return;
    setPropertiesLoading(true);
    setPropertyError(null);
    try {
      const r = await ga4.properties(websiteId);
      setProperties(r.data?.properties || []);
    } catch (err) {
      const code = err?.response?.status;
      if (code === 400) {
        // No refresh token stored — user needs to click Connect first.
        setProperties([]);
      } else if (code === 401) {
        setPropertyError(t("ga4.tokenRevoked"));
      } else {
        setPropertyError(err?.response?.data?.detail || t("ga4.propertiesError"));
      }
    } finally {
      setPropertiesLoading(false);
    }
  }, [websiteId, t]);

  useEffect(() => {
    if (needsProperty) fetchProperties();
  }, [needsProperty, fetchProperties]);

  async function onConnect() {
    if (!websiteId) return;
    setAuthorising(true);
    try {
      const r = await ga4.authorize(websiteId);
      const url = r.data?.authorize_url;
      if (url) window.location.href = url;
    } catch (err) {
      setOauthHint(`error:${err?.response?.data?.detail || "request_failed"}`);
    } finally {
      setAuthorising(false);
    }
  }

  async function onPickProperty(propertyId, propertyName) {
    if (!websiteId) return;
    setConnecting(true);
    setPropertyError(null);
    try {
      await ga4.connect(websiteId, {
        property_id: propertyId,
        property_name: propertyName,
      });
      await reloadStatus();
      setProperties(null); // hide the dropdown
    } catch (err) {
      setPropertyError(err?.response?.data?.detail || t("ga4.connectError"));
    } finally {
      setConnecting(false);
    }
  }

  async function onDisconnect() {
    if (!websiteId || !confirm(t("ga4.disconnectConfirm"))) return;
    setDisconnecting(true);
    try {
      await ga4.disconnect(websiteId);
      setReport(null);
      setProperties(null);
      await reloadStatus();
    } finally {
      setDisconnecting(false);
    }
  }

  const reloadReport = useCallback(async () => {
    if (!websiteId || !status?.connected) return;
    setReportLoading(true);
    setReportError(null);
    try {
      const r = await ga4.botTraffic(websiteId, {
        start: filters.start,
        end: filters.end,
        bot: botFilter,
      });
      setReport(r.data);
    } catch (err) {
      const code = err?.response?.status;
      if (code === 401) {
        setReportError("token_revoked");
        await reloadStatus();
      } else {
        setReportError(err?.response?.data?.detail || t("ga4.reportError"));
      }
    } finally {
      setReportLoading(false);
    }
  }, [websiteId, status?.connected, filters.start, filters.end, botFilter, reloadStatus, t]);

  useEffect(() => {
    reloadReport();
  }, [reloadReport]);

  if (!websiteId) {
    return <div className="text-slate-500">{t("prompts.pickWebsite")}</div>;
  }
  if (statusLoading) {
    return <div className="text-slate-500">{t("common.loading")}</div>;
  }

  return (
    <div className="space-y-6 max-w-5xl">
      <header>
        <h1 className="text-2xl font-semibold text-slate-900">{t("ga4.title")}</h1>
        <p className="text-sm text-slate-600">{t("ga4.subtitle")}</p>
      </header>

      <CallbackBanner hint={oauthHint} t={t} />

      {!status?.configured ? (
        <div className="card border-amber-200 bg-amber-50/70 text-sm text-amber-900">
          {t("ga4.notConfigured")}
        </div>
      ) : !status.connected && needsProperty ? (
        <PropertyPicker
          properties={properties}
          loading={propertiesLoading}
          error={propertyError}
          onPick={onPickProperty}
          connecting={connecting}
          t={t}
        />
      ) : !status.connected ? (
        <ConnectCard onConnect={onConnect} authorising={authorising} t={t} />
      ) : (
        <ConnectedHeader status={status} onDisconnect={onDisconnect} disconnecting={disconnecting} t={t} />
      )}

      {status?.connected && (
        <>
          <DashboardFilters
            value={filters}
            onChange={setFilters}
            disabled={reportLoading}
          />

          <BotFilter
            value={botFilter}
            onChange={setBotFilter}
            supported={report?.bots_supported || []}
            disabled={reportLoading}
            t={t}
          />

          {reportError === "token_revoked" ? (
            <div className="card border-red-200 bg-red-50/80 text-sm text-red-800 flex items-center gap-3">
              <span className="flex-1">{t("ga4.tokenRevoked")}</span>
              <button
                type="button"
                onClick={onConnect}
                className="btn-primary"
              >
                {t("ga4.reconnect")}
              </button>
            </div>
          ) : reportError ? (
            <div className="card border-red-200 bg-red-50/80 text-sm text-red-800">
              {reportError}
            </div>
          ) : null}

          {report ? (
            (report.summary || []).length === 0 ? (
              <EmptyBotTraffic t={t} />
            ) : (
              <>
                <BotTimeline
                  timeline={report.timeline || []}
                  supported={report.bots_supported || []}
                  t={t}
                />
                <BotTable rows={report.summary} t={t} />
              </>
            )
          ) : reportLoading ? (
            <div className="text-slate-500 text-sm">{t("common.loading")}</div>
          ) : null}
        </>
      )}
    </div>
  );
}

/* ─── Sub-components ───────────────────────────────────────────── */

function CallbackBanner({ hint, t }) {
  if (!hint) return null;
  if (hint === "connected") {
    return (
      <div className="card border-emerald-200 bg-emerald-50/80 text-sm text-emerald-900">
        {t("ga4.callbackConnected")}
      </div>
    );
  }
  // hint can be `error:xxx` — strip the prefix for the message.
  const code = hint.replace(/^error:/, "");
  return (
    <div className="card border-red-200 bg-red-50/80 text-sm text-red-800">
      {t("ga4.callbackError", { code })}
    </div>
  );
}

function ConnectCard({ onConnect, authorising, t }) {
  return (
    <div className="card space-y-3">
      <h2 className="text-sm font-semibold text-slate-900">{t("ga4.connectHeading")}</h2>
      <p className="text-sm text-slate-600">{t("ga4.connectBody")}</p>
      <button
        type="button"
        onClick={onConnect}
        disabled={authorising}
        className="btn-primary"
      >
        {authorising ? t("ga4.opening") : t("ga4.connectButton")}
      </button>
      <p className="text-xs text-slate-500">{t("ga4.scopeNote")}</p>
    </div>
  );
}

function PropertyPicker({ properties, loading, error, onPick, connecting, t }) {
  const [pid, setPid] = useState("");
  const list = properties || [];
  useEffect(() => {
    if (!pid && list.length > 0) setPid(list[0].property_id);
  }, [list, pid]);
  return (
    <div className="card space-y-3">
      <h2 className="text-sm font-semibold text-slate-900">
        {t("ga4.pickPropertyHeading")}
      </h2>
      <p className="text-sm text-slate-600">{t("ga4.pickPropertyBody")}</p>
      {loading ? (
        <div className="text-sm text-slate-500">{t("common.loading")}</div>
      ) : list.length === 0 ? (
        <div className="text-sm text-slate-500">{t("ga4.pickPropertyNone")}</div>
      ) : (
        <>
          <select
            className="input w-full"
            value={pid}
            onChange={(e) => setPid(e.target.value)}
            disabled={connecting}
          >
            {list.map((p) => (
              <option key={p.property_id} value={p.property_id}>
                {p.property_name} · {p.account_name}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => {
              const picked = list.find((p) => p.property_id === pid);
              if (picked) onPick(picked.property_id, picked.property_name);
            }}
            disabled={connecting || !pid}
            className="btn-primary"
          >
            {connecting ? t("ga4.saving") : t("ga4.savePropertyButton")}
          </button>
        </>
      )}
      {error && (
        <div className="text-sm text-red-700">{error}</div>
      )}
    </div>
  );
}

function ConnectedHeader({ status, onDisconnect, disconnecting, t }) {
  return (
    <div className="card flex flex-wrap items-center justify-between gap-3">
      <div>
        <div className="text-xs uppercase tracking-wider text-slate-500">
          {t("ga4.connectedProperty")}
        </div>
        <div className="text-sm font-medium text-slate-900 mt-0.5">
          {status.property_name || status.property_id}
        </div>
        {status.connected_at && (
          <div className="text-xs text-slate-500 mt-0.5">
            {t("ga4.connectedSince", {
              date: new Date(status.connected_at).toLocaleString(),
            })}
          </div>
        )}
      </div>
      <button
        type="button"
        onClick={onDisconnect}
        disabled={disconnecting}
        className="rounded-md px-3 py-2 text-sm ring-1 ring-slate-300 hover:bg-slate-50 disabled:opacity-50"
      >
        {disconnecting ? t("ga4.disconnecting") : t("ga4.disconnect")}
      </button>
    </div>
  );
}

function BotFilter({ value, onChange, supported, disabled, t }) {
  return (
    <div className="flex items-center gap-2">
      <label className="text-[10px] uppercase tracking-wider text-slate-500">
        {t("ga4.botFilter")}
      </label>
      <select
        className="input w-48"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
      >
        <option value="all">{t("ga4.allBots")}</option>
        {supported.map((b) => (
          <option key={b.key} value={b.key}>
            {b.label}
          </option>
        ))}
      </select>
    </div>
  );
}

function EmptyBotTraffic({ t }) {
  return (
    <div className="card text-sm text-slate-600 space-y-2">
      <div className="font-medium text-slate-900">{t("ga4.emptyTitle")}</div>
      <p>{t("ga4.emptyBody")}</p>
    </div>
  );
}

function BotTimeline({ timeline, supported, t }) {
  // Recharts wants every series as a top-level key on each row. The API
  // already gives us that shape (date + bot_key: sessions), so just feed
  // it directly.
  const keys = useMemo(() => {
    const seen = new Set();
    for (const row of timeline) {
      for (const k of Object.keys(row)) if (k !== "date") seen.add(k);
    }
    return Array.from(seen);
  }, [timeline]);

  return (
    <section className="card space-y-3">
      <div>
        <h3 className="text-sm font-semibold text-slate-900">
          {t("ga4.timelineTitle")}
        </h3>
        <p className="text-xs text-slate-500 mt-0.5">{t("ga4.timelineSubtitle")}</p>
      </div>
      <ResponsiveContainer width="100%" height={280}>
        <LineChart data={timeline} margin={{ top: 5, right: 10, bottom: 0, left: -15 }}>
          <CartesianGrid stroke="#eef2f7" vertical={false} />
          <XAxis
            dataKey="date"
            stroke="#94a3b8"
            fontSize={11}
            tickLine={false}
            axisLine={{ stroke: "#e2e8f0" }}
          />
          <YAxis
            stroke="#94a3b8"
            fontSize={11}
            tickLine={false}
            axisLine={false}
            allowDecimals={false}
          />
          <Tooltip
            contentStyle={{
              borderRadius: 8,
              border: "1px solid #e2e8f0",
              fontSize: 12,
              boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
            }}
          />
          <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 12 }} />
          {keys.map((k) => (
            <Line
              key={k}
              type="monotone"
              dataKey={k}
              stroke={colourForKey(k, supported)}
              strokeWidth={2.5}
              dot={{ r: 3, fill: colourForKey(k, supported), strokeWidth: 0 }}
              activeDot={{ r: 5 }}
              connectNulls
              name={supported.find((b) => b.key === k)?.label || k}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </section>
  );
}

function BotTable({ rows, t }) {
  return (
    <section className="card p-0 overflow-x-auto">
      <table className="w-full text-sm min-w-[520px]">
        <thead className="bg-slate-50 text-slate-600 text-left">
          <tr>
            <th className="px-4 py-3 font-medium">{t("ga4.colBot")}</th>
            <th className="px-4 py-3 font-medium">{t("ga4.colSessions")}</th>
            <th className="px-4 py-3 font-medium">{t("ga4.colPageViews")}</th>
            <th className="px-4 py-3 font-medium">{t("ga4.colLastSeen")}</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-200">
          {rows.map((row) => (
            <tr key={row.key}>
              <td className="px-4 py-3 font-medium text-slate-900">{row.bot}</td>
              <td className="px-4 py-3 mono">{row.sessions}</td>
              <td className="px-4 py-3 mono">{row.page_views}</td>
              <td className="px-4 py-3 text-slate-600">
                {row.last_seen || "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}
